/**
 * Z-index ladder — single source of truth for layering across the app.
 *
 * Components that establish their own stacking context should pick a
 * named slot from here rather than picking a magic integer.
 *
 * The numbers leave large gaps so that a future component can wedge in
 * between two slots (e.g. a "fullscreen camera overlay" that needs to
 * sit above modals but below tooltips) without re-numbering the world.
 *
 * Today the codebase has 25+ files hard-coding 1000/9999/10000/10001
 * — those will migrate opportunistically. New code should reach for
 * these tokens.
 */
export const zIndex = {
  /** Base content layer — leave at 'auto' / 0. */
  base: 0,

  /** Sticky elements: position-sticky headers, table-header rows. */
  sticky: 100,

  /** App-shell nav bar / global header. */
  appShell: 200,

  /** Dropdowns + popovers anchored to triggers in normal flow. */
  dropdown: 1000,

  /** Floating action buttons + non-modal "above the page" UI. */
  fixed: 1100,

  /**
   * Modal backdrops. The Modal component renders both the overlay and
   * the dialog inside a single fixed container at this z; the dialog
   * doesn't need its own slot.
   */
  modal: 9000,

  /** Popovers anchored to modal-internal elements (date pickers etc.). */
  modalPopover: 9100,

  /**
   * Confirmation dialogs a modal raises and portals to <body> (the account
   * impact preview). A body-level overlay must clear the modal that raised
   * it AND its popovers, or the click never lands and the save waits
   * forever; it still sits below fullscreen overlays and toasts.
   */
  modalConfirm: 9200,

  /**
   * Fullscreen overlays (camera viewfinder, splash, blocking spinner).
   * NOTE: ~a dozen legacy dialogs in apps/web hardcode 10000/10001
   * instead of using the modal token — treat 10001 as the app's
   * de-facto dialog ceiling until they're migrated.
   */
  overlay: 10000,

  /**
   * Toasts/snackbars must sit above every modal so error toasts
   * triggered from inside a modal are visible — including the legacy
   * dialogs hardcoding 10000/10001 (see overlay note).
   */
  toast: 10400,

  /** Tooltips — always the topmost layer so they're never clipped. */
  tooltip: 10500,
} as const;

export type ZIndex = typeof zIndex;
